/**
 * Centralized relative date formatting.
 *
 * Three styles:
 *  - "long"    → "2 hours ago", "3 days ago", "2 weeks ago"
 *  - "compact" → "2h ago", "3d ago", "2w ago"
 *  - "dated"   → relative only while fresh ("Just now", "15m ago",
 *                "4h ago"), then calendar dates: "Jul 16" within a year,
 *                "Sep 2024" beyond — table timestamp columns.
 *
 * All handle "just now" and fall back to a short date for anything older
 * than the format covers. For elapsed time with no suffix ("live for 3h"),
 * see `formatElapsedTime`; for a forward-looking countdown to a scheduled
 * moment, `formatUntil`.
 */

type Style = "long" | "compact" | "dated";

// Whole-unit counts for a millisecond delta (calendar-approximate months/years).
function unitCounts(diffMs: number) {
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  return {
    seconds,
    minutes,
    hours,
    days,
    weeks: Math.floor(days / 7),
    months: Math.floor(days / 30),
    years: Math.floor(days / 365),
  };
}

/**
 * Coarse elapsed time since `dateString` as a single largest unit with no
 * suffix — "14m", "3h", "9d" — for "live since / ran for" copy. Floors at
 * "moments" (<1 min or a future date) and caps at "Ny" with no date fallback.
 */
export function formatElapsedTime(dateString: string | null | undefined): string {
  if (!dateString) return "Never";
  const diffMs = Date.now() - new Date(dateString).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 60000) return "moments";
  const { minutes, hours, days, weeks, months, years } = unitCounts(diffMs);
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  if (days < 30) return `${weeks}w`;
  if (days < 365) return `${months}mo`;
  return `${years}y`;
}

/**
 * "42m" / "3h 12m" / "2d 4h" — a span at minute resolution, carrying its two
 * largest units, for the gap between scheduled events. `formatElapsedTime`
 * above is the single-unit twin for "live for 3h"; `formatDuration`
 * (format-duration.ts) is the millisecond-resolution one for how long a thing
 * took. Undefined below one minute — the caller decides what "less than a
 * minute" says in its own direction.
 */
export function formatSpan(ms: number): string {
  const { minutes, hours, days } = unitCounts(ms);
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${days}d ${hours % 24}h`;
}

/**
 * "in 42m" / "in 2d 4h" / "overdue by 3h 12m" — a scheduled timestamp relative
 * to now. Null when the timestamp does not parse.
 *
 * A time in the past reads as OVERDUE rather than rounding forward: for a
 * scheduled event, "the moment passed and nothing happened" is the whole
 * signal, and an "in <1m" that never resolves hides it indefinitely.
 */
export function formatUntil(iso: string): string | null {
  const diffMs = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(diffMs)) return null;
  if (diffMs < 0) {
    const overdueMs = -diffMs;
    return overdueMs < 60_000 ? "overdue" : `overdue by ${formatSpan(overdueMs)}`;
  }
  if (diffMs < 60_000) return "in <1m";
  return `in ${formatSpan(diffMs)}`;
}

export function formatRelativeDate(
  dateString: string | null | undefined,
  style: Style = "long",
  suffix = "ago",
): string {
  if (!dateString) return "Never";

  const diffMs = Date.now() - new Date(dateString).getTime();
  const { seconds, minutes, hours, days, weeks, months, years } = unitCounts(diffMs);

  if (diffMs < 0) return "Just now";

  if (style === "dated") {
    if (seconds < 60) return "Just now";
    if (minutes < 60) return `${minutes}m ${suffix}`;
    if (hours < 24) return `${hours}h ${suffix}`;
    const d = new Date(dateString);
    if (days < 365) {
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    }
    return d.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
  }

  if (style === "compact") {
    if (seconds < 60) return "now";
    if (minutes < 60) return `${minutes}m ${suffix}`;
    if (hours < 24) return `${hours}h ${suffix}`;
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days}d ${suffix}`;
    if (days < 30) return `${weeks}w ${suffix}`;
    if (days < 365) return `${months}mo ${suffix}`;
    return new Date(dateString).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  // long
  if (seconds < 60) return "just now";
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "min" : "mins"} ${suffix}`;
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ${suffix}`;
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} ${days === 1 ? "day" : "days"} ${suffix}`;
  if (days < 30) return `${weeks} ${weeks === 1 ? "week" : "weeks"} ${suffix}`;
  if (days < 365) return `${months} ${months === 1 ? "month" : "months"} ${suffix}`;
  return `${years} ${years === 1 ? "year" : "years"} ${suffix}`;
}
